// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React, { useState } from 'react';
import {
    Dialog,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Stack,
    Divider,
    Tooltip
} from '@mui/material';

// ==============================|| ANT DESIGN ICONS IMPORTS ||============================== //
import {
    CloseOutlined,
    DownloadOutlined,
    FilePdfOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined
} from '@ant-design/icons';

// ==============================|| PDF VIEWER IMPORTS ||============================== //
import { Viewer, Worker, SpecialZoomLevel, CharacterMap } from '@react-pdf-viewer/core';
import { zoomPlugin, RenderZoomInProps, RenderZoomOutProps } from '@react-pdf-viewer/zoom';
import { printPlugin, RenderPrintProps } from '@react-pdf-viewer/print';

// ==============================|| PDF VIEWER STYLES ||============================== //
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';

// ==============================|| CUSTOM ICONS ||============================== //
import { ModerIconZoomIn, ModernIconZoomOut, ModerPrintforPDFIcon, ModerPrintIcon } from 'utils/icons';

// ==============================|| TYPES & INTERFACES ||============================== //
export interface PdfPreviewProps {
    pdfUrl: string | null;
    pdfTitle: string;
    onClose: () => void;
    onDownload?: () => void;
}

const WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js';

const CHARACTER_MAP: CharacterMap = {
    isCompressed: true,
    url: 'https://unpkg.com/pdfjs-dist@3.4.120/cmaps/'
};

const PdfPreview: React.FC<PdfPreviewProps> = ({
    pdfUrl,
    pdfTitle,
    onClose,
    onDownload
}) => {
    const [isFullScreen, setIsFullScreen] = useState(false);

    const zoomPluginInstance = zoomPlugin();
    const { ZoomIn, ZoomOut } = zoomPluginInstance;

    const printPluginInstance = printPlugin();
    const { Print } = printPluginInstance;

    const isOpen = Boolean(pdfUrl);

    if (!isOpen) return null;

    const handleDownload = () => {
        if (onDownload) {
            onDownload();
        } else if (pdfUrl) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = pdfTitle || 'document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <Dialog
            open={isOpen}
            fullScreen={isFullScreen}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            maxWidth={isFullScreen ? false : "lg"}
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    bgcolor: '#0a0a0a',
                    color: '#fff',
                    borderRadius: isFullScreen ? 0 : '16px',
                    overflow: 'hidden',
                    boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.5)',
                    border: isFullScreen ? 'none' : '1px solid #2a2a2a',
                    height: isFullScreen ? '100vh' : '90vh',
                    maxHeight: isFullScreen ? '100vh' : '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease-in-out'
                }
            }}
            sx={{
                zIndex: 10000,
                '& .MuiDialog-backdrop': {
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)'
                }
            }}
        >
            {/* Header / Toolbar */}
            <Box
                sx={{
                    px: 2.5,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#141414',
                    borderBottom: '1px solid #222222',
                    flexShrink: 0
                }}
            >
                {/* Title */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, pr: 2 }}>
                    <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '20px', flexShrink: 0 }} />
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        noWrap
                        sx={{ color: '#fff', fontSize: '15px' }}
                    >
                        {pdfTitle || 'PDF Preview'}
                    </Typography>
                </Stack>

                {/* Actions (Zoom, Print, Download, Fullscreen, Close) */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {pdfUrl && (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{
                                bgcolor: '#1f1f1f',
                                borderRadius: '100px',
                                border: '1px solid #333',
                                px: 1.5,
                                py: 0.5
                            }}
                        >
                            {/* Zoom Out */}
                            <ZoomOut>
                                {(props: RenderZoomOutProps) => (
                                    <Tooltip title="Zoom Out" arrow PopperProps={{ sx: { zIndex: 11000 } }}>
                                        <IconButton
                                            size="small"
                                            onClick={props.onClick}
                                            sx={{ color: '#aaa', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
                                        >
                                            <ModernIconZoomOut />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </ZoomOut>

                            {/* Zoom In */}
                            <ZoomIn>
                                {(props: RenderZoomInProps) => (
                                    <Tooltip title="Zoom In" arrow PopperProps={{ sx: { zIndex: 11000 } }}>
                                        <IconButton
                                            size="small"
                                            onClick={props.onClick}
                                            sx={{ color: '#aaa', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
                                        >
                                            <ModerIconZoomIn />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </ZoomIn>

                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#333' }} />

                            {/* Print */}
                            <Print>
                                {(props: RenderPrintProps) => (
                                    <Tooltip title="Print PDF" arrow PopperProps={{ sx: { zIndex: 11000 } }}>
                                        <IconButton
                                            size="small"
                                            onClick={props.onClick}
                                            sx={{ color: '#aaa', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
                                        >
                                            <ModerPrintforPDFIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Print>

                            {/* Download */}
                            <Tooltip title="Download PDF" arrow PopperProps={{ sx: { zIndex: 11000 } }}>
                                <IconButton
                                    size="small"
                                    onClick={handleDownload}
                                    sx={{ color: '#aaa', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
                                >
                                    <DownloadOutlined style={{ fontSize: '18px' }} />
                                </IconButton>
                            </Tooltip>

                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#333' }} />

                            {/* Fullscreen Toggle */}
                            <Tooltip title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"} arrow PopperProps={{ sx: { zIndex: 11000 } }}>
                                <IconButton
                                    size="small"
                                    onClick={() => setIsFullScreen(prev => !prev)}
                                    sx={{ color: '#aaa', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
                                >
                                    {isFullScreen ? (
                                        <FullscreenExitOutlined style={{ fontSize: '18px' }} />
                                    ) : (
                                        <FullscreenOutlined style={{ fontSize: '18px' }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}

                    {/* Close Modal Button */}
                    <Tooltip title="Close" arrow PopperProps={{ sx: { zIndex: 11000 } }}>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: '#8c8c8c',
                                ml: 1,
                                '&:hover': {
                                    color: '#fff',
                                    bgcolor: 'rgba(255,255,255,0.08)'
                                }
                            }}
                        >
                            <CloseOutlined />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Document Content Area */}
            <Box
                sx={{
                    flexGrow: 1,
                    bgcolor: '#1a1a1a',
                    p: { xs: 1, sm: 2 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                {!pdfUrl ? (
                    <CircularProgress color="primary" />
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            bgcolor: '#525659',
                            borderRadius: isFullScreen ? 0 : '8px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                            '& .rpv-core__inner-page': {
                                margin: '0 auto !important',
                                WebkitFontSmoothing: 'antialiased',
                                MozOsxFontSmoothing: 'grayscale',
                                textRendering: 'optimizeLegibility'
                            }
                        }}
                    >
                        <Worker workerUrl={WORKER_URL}>
                            <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Viewer
                                    fileUrl={pdfUrl}
                                    characterMap={CHARACTER_MAP}
                                    plugins={[zoomPluginInstance, printPluginInstance]}
                                    defaultScale={SpecialZoomLevel.PageWidth}
                                />
                            </div>
                        </Worker>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default PdfPreview;
